import { h, defineComponent, VNodeChildren } from 'vue';
import {
  IssuePriority,
  IssueStatus,
  LegacyBreadcrumb,
  LegacyIssue,
} from '@/ui/components/inspectionReport/legacyTypes';
import { d, fromUnixTimestamp, t } from '@/ui/i18n/i18n';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';
import { InspectionItemType } from '@/entities/inspection/constants';
import { ApprovalStatus } from '@/entities/approvals/constants';

export interface ActionProps {
  item: LegacyIssue;
  displayQuery: boolean;
  displayTitles: boolean;
}

export const Action = defineComponent<ActionProps>({
  name: 'Action',
  props: {
    item: {
      type: Object,
      required: true,
    },
    displayQuery: {
      type: Boolean,
      default: true,
    },
    displayTitles: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const priority = {
      [IssuePriority.Medium]: t('inspection-report.medium'),
      [IssuePriority.High]: t('inspection-report.high'),
    }[props.item.priority];

    const approvalStatus
    = {
      [ApprovalStatus.Pending]: t('approval.status.pending'),
      [ApprovalStatus.Approved]: t('approval.status.approved'),
      [ApprovalStatus.Rejected]: t('approval.status.rejected'),
      [ApprovalStatus.ChangesRequested]: t('approval.status.changes-requested'),
    }[props.item.approvalStatus ?? ApprovalStatus.Pending];

    const status = {
      [IssueStatus.Open]: t('inspection-report.pending'),
      [IssueStatus.Solved]: t('inspection-report.done'),
      [IssueStatus.InProgress]: t('inspection-report.in-progress'),
      //legacy, still in DB, remove later
      [IssueStatus.CantDo]: t('inspection-report.cant-do'),
    }[props.item.status];

    const renderBox = (title: string, element: VNodeChildren | null, appendClass: string | null = null) => {
      return h(TableBox, {
        props: {
          appendClass,
        },
      }, [
        props.displayTitles ? h('div', title) : null,
        element,
      ]);
    };

    return function render() {
      const searchQuery = (breadcrumb: Array<LegacyBreadcrumb>) => {
        if (!breadcrumb) {
          return null;
        }

        const item = breadcrumb[breadcrumb.length - 1];

        if (item.type !== InspectionItemType.Conditional) {
          return item.title;
        }

        return item.parent?.title;
      };

      const query = props.displayQuery && props.item.payload && searchQuery(props.item.payload.breadcrumb);
      const associateAssignees = props.item.associateAssignees ? Object.values(props.item.associateAssignees).join(', ') : null;
      const assignees = props.item.assignees ? Object.values(props.item.assignees).join(', ') : null;
      const jointAssignees = [assignees, associateAssignees].filter(Boolean).join(', ');

      const getDateTime = (date: number) => {
        if (!date) {
          return [];
        }

        const formatedDate = d(fromUnixTimestamp(date) as unknown as Date, 'shortDateTime');
        if (['AM', 'PM'].some(timeFormat => formatedDate.includes(timeFormat))) {
          // eslint-disable-next-line no-useless-escape
          return formatedDate.replace(/\ /, '_').split('_');
        }

        return [formatedDate];
      };

      return h('tr', { attrs: { 'data-test': 'actions-tr' } }, [
        renderBox(`${t('inspection-report.action')}:`, props.item.title as string,
          'inspection-report__action-table-extended-row'),
        renderBox(`${t('inspection-report.deadline')}:`,
          props.item.due_at
            ? [h('span', getDateTime(props.item.due_at as number).map(dateTimePart => h('div', [dateTimePart])))]
            : null),
        renderBox(`${t('inspection-report.assignee')}:`, jointAssignees, 'inspection-report__action-table-extended-row'),
        renderBox(`${t('inspection-report.status')}:`, status ?? '',
          'inspection-report__action-table-extended-row'),
        renderBox(`${t('inspection-report.priority')}:`, priority),
        props.item.approvals_enabled
          ? renderBox(`${t('inspection-report.approval.status')}:`, approvalStatus)
          : null,
        renderBox(`${t('inspection-report.query')}:`, query ?? '-', 'inspection-report__action-table-extended-row'),
      ]);
    };
  },
});
